import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useEnrollment } from '@hooks/useEnrollment'
import { GET_LINEAR_PROGRESS_GATE } from '@services/api/graphql/queries/getLinearProgressGate'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface IBlockedCourseContent {
  courseContentId: string
  requiredLessonId: string | null
}

/**
 * Gate do progresso linear: quem decide é o servidor
 * (`Enrollment.blockedCourseContents`), que enxerga o curso inteiro.
 *
 * Antes o members inferia a "aula anterior" no cliente, percorrendo a árvore de módulos
 * já carregada — e a lista de conteúdos vem paginada de 10 em 10, então em módulo maior
 * do que a página o gate comparava com a 10ª aula e liberava cedo (HET-4001).
 *
 * O progresso LOCAL ainda tem uma palavra: o servidor lê as conclusões do seshat, que
 * ingere por fila, e por ~1,4s depois de concluir a aula (medido em staging) ele ainda
 * responde o estado anterior. Quando a aula que destrava o conteúdo já consta concluída
 * aqui — inclusive pela atualização otimista — o cadeado sai na hora. A conta é um `&&`:
 * nunca bloqueia além do que o servidor mandou, só antecipa o que já é verdade.
 *
 * `errorPolicy: 'all'` e a query separada são a rede de segurança: se o core ainda não
 * tiver o campo (janela de deploy) ou a leitura falhar, esta query falha sozinha, nada
 * fica bloqueado e o resto da tela segue de pé.
 */
export function useLinearProgressGate() {
  const { enrollmentId, courseId } = useCourseRouteParams()
  const { isPreview } = useAdminPreview()
  const { enrollment } = useEnrollment()

  const { data } = useQuery(GET_LINEAR_PROGRESS_GATE, {
    variables: { id: `${enrollmentId}`, courseId: `${courseId}` },
    // Sem matrícula (preview do produtor) nada é bloqueado, como no legado.
    skip: isPreview || !enrollmentId || !courseId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  const serverBlocked = data?.enrollment?.blockedCourseContents
  const lessonsProgresses = enrollment?.progressCard?.lessonsProgresses

  const blockedContents = useMemo<IBlockedCourseContent[]>(
    () => serverBlocked ?? [],
    [serverBlocked],
  )

  const completedLessonIds = useMemo(
    () =>
      new Set<string>(
        (lessonsProgresses ?? [])
          .filter((lessonProgress) => lessonProgress.completed)
          .map((lessonProgress) => String(lessonProgress.lessonId)),
      ),
    [lessonsProgresses],
  )

  const blockedSet = useMemo(
    () =>
      new Set<string>(
        blockedContents
          .filter(
            (blocked) =>
              !blocked.requiredLessonId ||
              !completedLessonIds.has(String(blocked.requiredLessonId)),
          )
          .map((blocked) => String(blocked.courseContentId)),
      ),
    [blockedContents, completedLessonIds],
  )

  return useMemo(
    () => ({
      /** O id do course_content é `number` no DTO e `ID` (String) no GraphQL. */
      isContentBlocked: (courseContentId: number | string) =>
        blockedSet.has(String(courseContentId)),
    }),
    [blockedSet],
  )
}
